import { Icon } from '@iconify/react';
import * as RadixSelect from '@radix-ui/react-select';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useId, useRef, useState } from 'react';
import { scrollToElementWithOffset } from '~/utils/scroll';

interface IconPickerProps {
  name: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
}

const COLS = 11;
const ROW_HEIGHT = 38;

export function IconPicker(props: IconPickerProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [icons, setIcons] = useState<{ body: string; value: string; label: string }[]>([]);

  const virtualizer = useVirtualizer({
    count: Math.ceil(icons.length / COLS),
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 2,
  });

  const loadIcons = async () => {
    const { default: lucideIcons } = await import('@iconify-json/lucide/icons.json');

    const iconData = Object.entries(lucideIcons.icons).map(([name, data]) => ({
      body: (data as { body: string }).body,
      value: `i-lucide:${name}`,
      label: name,
    }));

    setIcons(iconData);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    loadIcons();
  }, [open]);

  return (
    <RadixSelect.Root
      value={props.value}
      name={props.name}
      onOpenChange={setOpen}
      open={open}
      onValueChange={(value) => {
        if (value) {
          props.onValueChange(value);
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor={id} className="text-bolt-ds-textSecondary font-medium text-xs">
          {props.label}
        </label>
        <RadixSelect.Trigger
          id={id}
          className="flex items-center data-[placeholder]:text-bolt-elements-textSecondary justify-between border border-bolt-elements-borderColor focus:ring-2 focus:ring-bolt-ds-brandHighlight text-sm gap-2 h-10 bg-bolt-elements-background-depth-2 px-4 rounded-lg focus:outline-none"
          onFocus={(event) => scrollToElementWithOffset(event.target.parentElement?.parentElement?.parentElement, 24)}
        >
          <RadixSelect.Value placeholder="-">
            {props.value && <Icon icon={props.value.slice(2)} className="size-5" />}
          </RadixSelect.Value>
          <RadixSelect.Icon asChild>
            <span className="i-ph:caret-down inline-block text-bolt-elements-icn" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            onEscapeKeyDown={(event) => {
              event.stopPropagation();
              setOpen(false);
            }}
            position="popper"
            sideOffset={8}
            className="z-50 bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor rounded-lg shadow-lg w-[456px] max-h-[320px] overflow-hidden"
          >
            <RadixSelect.Viewport ref={containerRef} className="p-2 h-[320px] overflow-auto">
              <RadixSelect.SelectGroup
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => (
                  <div
                    key={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${ROW_HEIGHT}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
                    }}
                    className="grid gap-0.5"
                  >
                    {icons.slice(virtualRow.index * COLS, virtualRow.index * COLS + COLS).map((icon) => (
                      <RadixSelect.Item
                        key={icon.value}
                        value={icon.value}
                        className="flex flex-col items-center justify-center rounded hover:bg-bolt-elements-background-depth-3 focus:bg-bolt-elements-background-depth-3 focus:outline-none data-[state=checked]:bg-bolt-elements-background-depth-3 cursor-pointer"
                      >
                        <Icon icon={icon.value.slice(2)} className="size-5" />
                        <RadixSelect.ItemText>
                          <div className="sr-only">{icon.label}</div>
                        </RadixSelect.ItemText>
                      </RadixSelect.Item>
                    ))}
                  </div>
                ))}
              </RadixSelect.SelectGroup>
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </div>
    </RadixSelect.Root>
  );
}
